import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { getCollaboratorPortalCustomerHistory } from '../../../../services/PublicBookingService';
import { formatDate, formatDaysAgo } from '../../../../utils/DateUtils';
import { formatQuantity } from './portalFormat';
import './PortalCustomerHistory.css';

/** "Pomada" ou "2x Pomada": a quantidade só aparece quando passa de um. */
export const formatVisitItems = (items) => (items || [])
    .map((item) => (Number(item.quantity) > 1 ? `${formatQuantity(item.quantity)}x ${item.name}` : item.name))
    .join(' + ');

/** Quem atendeu: "com você" quando foi o próprio colaborador; venda sem profissional fica sem nada. */
export const formatVisitEmployee = (visit) => {
    if (visit.ownVisit) return 'com você';
    return visit.employeeName ? `com ${visit.employeeName}` : null;
};

/**
 * Últimas visitas do cliente no detalhe do atendimento do portal: o que ele fez e levou em cada
 * dia, para o colaborador lembrar do cliente antes de atender.
 *
 * Carrega só quando o detalhe abre, e não junto da agenda: a semana tem dezenas de
 * atendimentos e o histórico só interessa no que foi tocado. A lista tem altura limitada e rola
 * por dentro, para o detalhe não virar uma tela comprida no celular.
 *
 * @param {string} portalToken - Token do portal.
 * @param {string} appointmentUuid - Atendimento aberto; ele mesmo fica fora do histórico.
 * @param {function} onSessionExpired - Chamado quando o token do portal venceu (401).
 */
const PortalCustomerHistory = ({ portalToken, appointmentUuid, onSessionExpired }) => {

    const [visits, setVisits] = useState([]);
    const [status, setStatus] = useState('loading');

    useEffect(() => {
        if (!portalToken || !appointmentUuid) return undefined;

        // A resposta de um atendimento já fechado não pode preencher o próximo que for aberto.
        let active = true;
        setStatus('loading');
        setVisits([]);

        getCollaboratorPortalCustomerHistory(portalToken, appointmentUuid)
            .then(({ data }) => {
                if (!active) return;
                setVisits(Array.isArray(data) ? data : []);
                setStatus('ready');
            })
            .catch((error) => {
                if (!active) return;
                if (error?.response?.status === 401) {
                    onSessionExpired?.();
                    return;
                }
                setStatus('error');
            });

        return () => { active = false; };
        // onSessionExpired fica de fora: a página passa uma função nova a cada render, e
        // recarregar o histórico por isso refaria a consulta sem motivo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portalToken, appointmentUuid]);

    return (
        <section className="employee-appointment-section-box portal-customer-history" aria-label="Últimas visitas">
            <span className="employee-appointment-section-title">
                <i className="fa-solid fa-clock-rotate-left me-1" aria-hidden="true"></i> Últimas visitas
            </span>

            {status === 'loading' && (
                <div className="portal-customer-history__status">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Carregando visitas…
                </div>
            )}

            {status === 'error' && (
                <div className="portal-customer-history__status">Não foi possível carregar as visitas.</div>
            )}

            {status === 'ready' && visits.length === 0 && (
                <div className="portal-customer-history__status">Nenhuma visita anterior.</div>
            )}

            {status === 'ready' && visits.length > 0 && (
                <ol className="portal-customer-history__list">
                    {visits.map((visit, index) => {
                        const employee = formatVisitEmployee(visit);
                        return (
                            <li className="portal-customer-history__item" key={`${visit.date}-${index}`}>
                                <div className="portal-customer-history__head">
                                    <strong>{formatDate(visit.date)}</strong>
                                    <span>{formatDaysAgo(visit.date)}</span>
                                    {employee && <span className="portal-customer-history__employee">{employee}</span>}
                                </div>
                                {visit.services?.length > 0 && (
                                    <p className="portal-customer-history__line">
                                        <i className="fa-solid fa-clipboard-list" aria-hidden="true"></i>
                                        <span>
                                            <span className="visually-hidden">Serviços: </span>
                                            {formatVisitItems(visit.services)}
                                        </span>
                                    </p>
                                )}
                                {visit.products?.length > 0 && (
                                    <p className="portal-customer-history__line portal-customer-history__line--products">
                                        <i className="fa-solid fa-bag-shopping" aria-hidden="true"></i>
                                        <span>
                                            <span className="visually-hidden">Produtos: </span>
                                            {formatVisitItems(visit.products)}
                                        </span>
                                    </p>
                                )}
                            </li>
                        );
                    })}
                </ol>
            )}
        </section>
    );
};

export default PortalCustomerHistory;
